import { PiArrowLeftBold } from 'react-icons/pi';
import { Button } from '@ewf/components/Button';
import { SunkenBox } from '@ewf/components/SunkenBox';
import { CopyAddressButton } from '@ewf/components/ModalUI/CopyAddressButton';
import { Stepper } from '@ewf/components/ModalUI/Stepper';
import { TransactionProgress } from '@ewf/components/ModalUI/TransactionProgress';
import { TransactionSummary } from '@ewf/components/ModalUI/TransactionSummary';
import { CloseButton } from '@ewf/components/ModalUI/CloseButton';
import { LIFTING_CHAINS, MARKETPLACE_CHAINS } from '@shared/types/enums';
import { Balance } from '@ewf/stores/balance';
import { truncateBalance } from '@ewf/lib/utils';
import { displayBalance, formatGasFee } from '@ewf/utils/balance-helpers';
import { clsx } from 'clsx';

export const Confirm = ({
  fee,
  amount,
  address = '',
  network,
  balance,
  onConfirm,
  onGoBack,
  onClose,
}: Props): JSX.Element => {
  const isEthNetwork = network === MARKETPLACE_CHAINS.ETH;
  const gasToken = isEthNetwork ? 'ETH' : 'EWT';

  // Gas balance validation for ETH network
  const getGasBalanceStatus = () => {
    if (!isEthNetwork || !balance?.gas) return null;

    const ethBalance = parseFloat(balance.gas.token);
    const buffer = fee * 0.3; // 30% buffer for gas price fluctuations
    const gasWithBuffer = fee + buffer;

    if (ethBalance === 0) {
      return {
        status: 'insufficient',
        message: 'Insufficient ETH for gas fees',
      };
    } else if (ethBalance > 0 && ethBalance < gasWithBuffer) {
      return {
        status: 'warning',
        message: 'ETH balance may be insufficient for gas fees',
      };
    } else {
      return {
        status: 'sufficient',
        message: 'Sufficient ETH for gas fees',
      };
    }
  };

  const gasBalanceStatus = getGasBalanceStatus();

  return (
    <div className="w-[550px] rounded-lg border-2 border-transparent p-8 gradient-border-secondary-with-gray-90">
      <section className="relative mb-8 flex h-[40px] justify-between">
        <button
          onClick={() => onGoBack(amount)}
          className="grid h-[40px] w-[40px] place-items-center rounded-full bg-gray-100 text-font hover:bg-gray-95 active:bg-black"
        >
          <PiArrowLeftBold size={24} />
        </button>
        <CopyAddressButton address={address} />
        <CloseButton onClick={onClose} />
      </section>
      <section className="mb-8">
        <Stepper step={3} maxStep={3} />
      </section>
      <section className="mb-8 rounded-lg p-6 bg-popup-gradient">
        <TransactionProgress operation="lifting" step="confirm" />
        <SunkenBox className="mb-6 text-center font-bold">Confirm transaction</SunkenBox>
        <TransactionSummary amount={amount} operation="lifting" fromChain={network} />
      </section>
      <section className="mb-8 rounded-lg p-6 bg-popup-gradient">
        <div className="grid grid-cols-2 gap-2">
          <p>Network fees</p>
          <p>
            {(() => {
              const formatted = formatGasFee(fee, gasToken);
              return `${formatted.value} ${formatted.unit}`;
            })()}
          </p>
          {isEthNetwork && (
            <>
              <p>Network balance</p>
              <p>
                {displayBalance(truncateBalance(balance?.gas?.token || 0))} {balance?.gas?.symbol}
              </p>
            </>
          )}
        </div>
        {gasBalanceStatus && (
          <div className="mt-3 flex items-center gap-2 text-sm ">
            <span className="relative flex size-3">
              <span
                className={clsx(
                  'absolute inline-flex h-full w-full animate-ping rounded-full opacity-75',
                  gasBalanceStatus.status === 'insufficient'
                    ? 'bg-red'
                    : gasBalanceStatus.status === 'warning'
                      ? 'bg-orange'
                      : 'bg-green',
                )}
              />
              <span
                className={clsx(
                  `relative inline-flex size-3 rounded-full`,
                  gasBalanceStatus.status === 'insufficient'
                    ? 'bg-red'
                    : gasBalanceStatus.status === 'warning'
                      ? 'bg-orange'
                      : 'bg-green',
                )}
              />
            </span>
            <span
              className={clsx(
                gasBalanceStatus.status === 'insufficient'
                  ? 'text-red'
                  : gasBalanceStatus.status === 'warning'
                    ? 'text-orange'
                    : 'text-green',
              )}
            >
              {gasBalanceStatus.message}
            </span>
          </div>
        )}
      </section>
      <section className="flex justify-center">
        <Button onClick={onConfirm} disabled={gasBalanceStatus?.status === 'insufficient'}>
          Confirm
        </Button>
      </section>
    </div>
  );
};

interface Props {
  fee: number;
  amount: number;
  address: string;
  network: LIFTING_CHAINS;
  balance?: Balance;
  onConfirm: () => void;
  onGoBack: (amount: number) => void;
  onClose: () => void;
}
